import { isToday, isYesterday, isWithinLastDays, isThisYear, Locale } from '..';

const oneMinute: number = 1000 * 60;
const oneHour: number = oneMinute * 60;
const justNowMinutesThreshold: number = 2;

export interface RelativeDateProps {
  /**
   * The locale used for localizing strings.
   */
  locale: Locale;

  /**
   * The string table for more complex strings.
   */
  stringTable: RelativeDateStringTable;

  /**
   * Consider returning a lower cased first letter if the string will be used in the middle of a sentence.
   */
  lowerCaseFirstLetter?: boolean;

  /**
   * Whether or not to use long format for dates
   */
  longFormat?: boolean;
}

export interface RelativeDateStringTable {
  relativeTimestampJustNow: string;
  relativeTimestampMinutesAgo: (placeholders: { minutes: number }) => string;
  relativeTimestampHoursAgo: (placeholders: { hours: number }) => string;
  relativeTimestampYesterday: (placeholders: { time: string }) => string;
  relativeTimestampWithinLastDays: (placeholders: { day: string; time: string }) => string;
  relativeTimestampJustNowLowerCase: string;
  relativeTimestampYesterdayLowerCase: (placeholders: { time: string }) => string;
  relativeTimestampWithinCurrentYear?: (placeholders: { day: string; time: string }) => string;
}

export function getRelativeDateFromTimestamp(timestamp: number, props: RelativeDateProps): string {
  const now = new Date(Date.now());
  const diffInMilliseconds = now.getTime() - timestamp;
  const diffInMinutes = Math.floor(diffInMilliseconds / oneMinute);

  const { locale, longFormat, lowerCaseFirstLetter, stringTable } = props;

  if (diffInMinutes <= justNowMinutesThreshold) {
    return lowerCaseFirstLetter ? stringTable.relativeTimestampJustNowLowerCase : stringTable.relativeTimestampJustNow;
  }

  if (diffInMinutes < 60) {
    return stringTable.relativeTimestampMinutesAgo({ minutes: diffInMinutes });
  }

  const date = new Date(timestamp);
  if (isToday(date)) {
    const diffInHours = Math.floor(diffInMilliseconds / oneHour);
    return stringTable.relativeTimestampHoursAgo({ hours: diffInHours });
  }

  const time = date.toLocaleString(locale, {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true
  });

  if (isYesterday(date)) {
    return lowerCaseFirstLetter
      ? stringTable.relativeTimestampYesterdayLowerCase({ time })
      : stringTable.relativeTimestampYesterday({ time });
  }

  if (isWithinLastDays(date, 7)) {
    const day = date.toLocaleDateString(locale, {
      weekday: longFormat ? 'long' : 'short'
    });
    return stringTable.relativeTimestampWithinLastDays({ day, time });
  }

  if (isThisYear(date)) {
    const day = date.toLocaleDateString(locale, {
      day: 'numeric',
      month: longFormat ? 'long' : 'short'
    });

    if (stringTable.relativeTimestampWithinCurrentYear) {
      return stringTable.relativeTimestampWithinCurrentYear({ day, time });
    }

    return day;
  }

  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: longFormat ? 'long' : 'short',
    year: 'numeric'
  });
}
